import React, { useMemo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';

interface ModelPerformance {
  model_name: string;
  accuracy: number;
  rmse: number;
  mae: number;
  r_squared: number;
}

interface ModelComparisonRadarProps {
  models: ModelPerformance[];
  loading?: boolean;
}

const ModelComparisonRadar: React.FC<ModelComparisonRadarProps> = ({ models, loading }) => {
  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const radarData = useMemo(() => {
    const metrics = ['Accuracy', 'RMSE', 'MAE', 'R²'];
    return metrics.map(metric => {
      const dataPoint: any = { metric };
      models.slice(0, 3).forEach(model => {
        switch(metric) {
          case 'Accuracy':
            dataPoint[model.model_name] = model.accuracy;
            break;
          case 'RMSE':
            dataPoint[model.model_name] = 100 - model.rmse; // Invert for radar
            break;
          case 'MAE':
            dataPoint[model.model_name] = 100 - model.mae; // Invert for radar
            break;
          case 'R²':
            dataPoint[model.model_name] = model.r_squared * 100;
            break;
        }
      });
      return dataPoint;
    });
  }, [models]);

  const colors = ['#6366f1', '#10b981', '#f59e0b'];

  return (
    <div className="w-full h-48 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={radarData}>
          <PolarGrid stroke="#e2e8f0" />
          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#64748b' }} />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]} 
            tick={{ fontSize: 10, fill: '#64748b' }}
          />
          {models.slice(0, 3).map((model, index) => (
            <Radar
              key={model.model_name}
              name={model.model_name}
              dataKey={model.model_name}
              stroke={colors[index]}
              fill={colors[index]}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ModelComparisonRadar;